/*
 * Copyright (c) 2017, Indigen Solutions. All rights reserved.
 *
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 *
 * This software is the confidential and proprietary information.
 *
 * You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement
 * you entered into.
 */

import axios from "axios";
import { AxiosRequestConfig  } from "axios";

import { Resource } from "./Resource";
import { Model } from "./Model";
import { Document } from "./Document";
import { ModelConstructor } from "./ModelConstructor";
import { DocumentBuilder } from "./DocumentBuilder";

/**
 *
 */
export class JsonApiService {

    private modelConstructors: {[type: string]: ModelConstructor} = {};
    private baseUrl: string;
    private authToken: string = null;

    /**
     *
     * @param {string} baseUrl
     * @returns {JsonApiService}
     */
    setBaseUrl(baseUrl: string) {
        this.baseUrl = baseUrl;

        return this;
    }

    /**
     *
     * @returns {string}
     */
    getBaseUrl(): string {
        if ( !this.baseUrl ) {
            throw new Error("invalid base url, call setBaseUrl first");
        }

        return this.baseUrl;
    }

    /**
     *
     * @param {string} authToken
     * @returns {JsonApiService}
     */
    setAuthToken(authToken: string): JsonApiService {
        this.authToken = authToken;

        return this;
    }

    /**
     *
     * @param {string} type
     * @param {ModelConstructor} constructor
     * @returns {JsonApiService}
     */
    registerModel(type: string, constructor: ModelConstructor): JsonApiService {
        this.modelConstructors[type] = constructor;

        return this;
    }

    /**
     *
     * @param {string} type
     * @returns {ModelConstructor}
     */
    getModelConstructor(type: string): ModelConstructor {
        if ( !this.modelConstructors[type] ) {
            throw new Error(`Can't find model for type ${type}`);
        }

        return this.modelConstructors[type];
    }

    /**
     *
     * @param {string} url
     * @param {{[p: string]: string}} params
     * @returns {Promise<Document<T extends Model>>}
     */
    get<T extends Model>(url: string, params: { [name: string]: any } = null): Promise<Document<T>> {
        return this._sendRequest<T>(url, "GET", null, params);
    }

    /**
     *
     * @param {string} url
     * @param {Document<Model>} document
     * @returns {Promise<Document<T extends Model>>}
     */
    post<T extends Model>(url: string, document: Document<Model> = null): Promise<Document<T>> {
        return this._sendRequest<T>(url, "POST", document);
    }

    /**
     *
     * @param {string} url
     * @param {Document<Model>} document
     * @returns {Promise<Document<T extends Model>>}
     */
    patch<T extends Model>(url: string, document: Document<Model> = null): Promise<Document<T>> {
        return this._sendRequest<T>(url, "PATCH", document);
    }

    /**
     *
     * @param {string} type
     * @param {{[key in keyof T]:T[key]}} base
     * @returns {T}
     */
    buildModel<T>(type: string, base: {[key in keyof T]: T[key]} = null): T {
        const model = new (this.getModelConstructor(type))();
        return Object.assign(model, base);
    }

    /**
     *
     * @param {string} type
     * @param {{[key in keyof T]:T[key]}} base
     * @returns {Resource<T extends Model>}
     */
    buildResource<T extends Model>(type: string, base: {[key in keyof T]: T[key]} = null): Resource<T> {
        return new Resource(type, this.buildModel<T>(type, base));
    }

    /**
     *
     * @param json
     * @returns {Document<T extends Model>}
     */
    parseDocument<T extends Model>(json: any): Document<T> {
        return new DocumentBuilder<T>(json, this).getDocument();
    }

    /* Implementation. */

    private _getHttpHeaders() {
        let headers: any = {
            "Content-Type": "application/vnd.api+json"
        };

        if ( this.authToken != null) {
            headers.Authorization = `JWT ${this.authToken}`;
        }

        return headers;
    }

    private _sendRequest<T extends Model>(url: string, method: string,
                                          document: Document<Model> = null,
                                          params: { [name: string]: string } = null): Promise<Document<T>> {

        const config: AxiosRequestConfig = {
            url,
            baseURL: this.baseUrl,
            method: method,
            headers: this._getHttpHeaders()
        };

        if ( document ) {
            config.data = document.toRaw();
        }

        if ( params ) {
            config.params = params;
        }

        return axios.request(config)
            .catch(error => {
                if ( error.response && error.response.data ) {
                    return error.response;
                } else {
                    throw error;
                }
            })
            .then(response => this.parseDocument<T>(response.data))
        ;
    }

}



// WEBPACK FOOTER //
// ./src/utils/json-api/JsonApiService.ts